import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import socket from "./../ScoreboardAdmin/socketClient";
import DetermineWinnerModalNeWaza from "./DetermineWinnerModalNeWaza";

import {
  addAdvantage,
  addPenalty,
  addTechnique,
  calculateNeWazaResult,
  clearTerminalStates,
  createEmptyNeWazaScore,
  getNeWazaPoints,
  isDisqualified,
  removeAdvantage,
  removePenalty,
  removeTechnique,
  setDisqualification,
  setForfait,
  setMedicalStop,
  setSubmission,
} from "./../../utils/neWazaScoring";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const getCompetiteurDisplayName = (
  competiteur,
  fallbackLabel = "Combattant",
) => {
  if (!competiteur) return fallbackLabel;
  const nom = competiteur.nom_competiteur || competiteur.nom || "";
  const prenom = competiteur.prenom_competiteur || competiteur.prenom || "";
  const full = `${nom} ${prenom}`.trim();
  return full || fallbackLabel;
};

const getCompetiteurClub = (competiteur) => {
  if (!competiteur) return "";
  return String(
    competiteur.club_competiteur || competiteur.club || competiteur.nom_club || "",
  ).trim();
};

const formatSeconds = (sec) => {
  const total = Math.max(0, Number(sec) || 0);
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

const sanitizeCombatLabel = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d+_\d+$/.test(raw)) return "";
  return raw;
};

const getDefaultDurationFromCategory = (categorieAge = "") => {
  const raw = String(categorieAge || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (raw.includes("cadet")) return 240;
  return 300;
};

const clearOneSideTerminalStates = (score = {}) => ({
  ...score,
  submission: false,
  disqualified: false,
  penaltyDisqualification: false,
  forfait: false,
  medicalStop: false,
});

const getSimpleReasonLabel = (result, redScore, blueScore, isGoldenScore) => {
  if (redScore?.forfait || blueScore?.forfait) return "Forfait";
  if (redScore?.medicalStop || blueScore?.medicalStop) return "Arrêt médical";
  if (redScore?.submission || blueScore?.submission) return "Ippon";
  if (isDisqualified(redScore) || isDisqualified(blueScore)) {
    return "Disqualification";
  }
  if (result?.needGoldenScore) return "Golden score";
  if (result?.needRefereeDecision) return "Décision arbitrale";
  if (isGoldenScore) return "Golden score";
  return "Combat en cours";
};

function buildDetailsCombatNeWaza({
  redScore,
  blueScore,
  result,
  competiteur1,
  competiteur2,
  chrono,
  chronoMax,
  isGoldenScore = false,
  medicalCallActive = false,
  videoReviewActive = false,
}) {
  const redName = getCompetiteurDisplayName(competiteur1, "Rouge");
  const blueName = getCompetiteurDisplayName(competiteur2, "Bleu");

  const redPts = getNeWazaPoints(redScore);
  const bluePts = getNeWazaPoints(blueScore);

  const winnerColor = result?.winnerColor || null;

  let gagnant = "À déterminer";
  if (winnerColor === "red") gagnant = `${redName} (Rouge)`;
  if (winnerColor === "blue") gagnant = `${blueName} (Bleu)`;

  const reason = getSimpleReasonLabel(
    result,
    redScore,
    blueScore,
    isGoldenScore,
  );

  return [
    `Gagnant : ${gagnant}`,
    `Discipline : Ne-Waza`,
    `Motif : ${reason}`,
    `Score : Rouge ${redPts.total} – Bleu ${bluePts.total}`,
    `Avantages : Rouge ${redScore?.advantages || 0} – Bleu ${blueScore?.advantages || 0}`,
    `Pénalités : Rouge ${redScore?.penalties || 0} – Bleu ${blueScore?.penalties || 0}`,
    `Actions +2 : Rouge ${
      (redScore?.takedown2 || 0) +
      (redScore?.kneeOnBelly2 || 0) +
      (redScore?.sweep2 || 0) +
      (redScore?.other2 || 0)
    } – Bleu ${
      (blueScore?.takedown2 || 0) +
      (blueScore?.kneeOnBelly2 || 0) +
      (blueScore?.sweep2 || 0) +
      (blueScore?.other2 || 0)
    }`,
    `Actions +3 : Rouge ${(redScore?.guardPass3 || 0) + (redScore?.other3 || 0)} – Bleu ${(blueScore?.guardPass3 || 0) + (blueScore?.other3 || 0)}`,
    `Actions +4 : Rouge ${(redScore?.mount4 || 0) + (redScore?.backControl4 || 0) + (redScore?.other4 || 0)} – Bleu ${(blueScore?.mount4 || 0) + (blueScore?.backControl4 || 0) + (blueScore?.other4 || 0)}`,
    `États : Rouge${redScore?.submission ? " Ippon" : ""}${isDisqualified(redScore) ? " disqualification" : ""}${redScore?.medicalStop ? " arrêt médical" : ""}${redScore?.forfait ? " forfait" : ""} · Bleu${blueScore?.submission ? " Ippon" : ""}${isDisqualified(blueScore) ? " disqualification" : ""}${blueScore?.medicalStop ? " arrêt médical" : ""}${blueScore?.forfait ? " forfait" : ""}`,
    `Temps : ${
      isGoldenScore
        ? `Golden score ${formatSeconds(chrono)}`
        : `${formatSeconds(chrono)} / ${formatSeconds(chronoMax)}`
    }`,
    `Appel médical actif : ${medicalCallActive ? "oui" : "non"}`,
    `Challenge vidéo actif : ${videoReviewActive ? "oui" : "non"}`,
  ].join("\n");
}

/* -------------------------------------------------------------------------- */
/* UI                                                                         */
/* -------------------------------------------------------------------------- */

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-white/8 text-white border-white/10",
    emerald: "bg-emerald-500/15 text-emerald-200 border-emerald-400/20",
    yellow: "bg-yellow-400/15 text-yellow-200 border-yellow-300/20",
    red: "bg-red-500/15 text-red-200 border-red-400/20",
    blue: "bg-blue-500/15 text-blue-200 border-blue-400/20",
    rose: "bg-rose-500/15 text-rose-200 border-rose-400/20",
    orange: "bg-orange-500/15 text-orange-200 border-orange-400/20",
    cyan: "bg-cyan-500/15 text-cyan-200 border-cyan-400/20",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function ActionButton({
  children,
  onClick,
  variant = "dark",
  disabled = false,
  className = "",
}) {
  const variants = {
    dark: "bg-slate-800 text-white hover:bg-slate-700 border border-white/10",
    green: "bg-emerald-500 text-slate-950 hover:bg-emerald-400",
    red: "bg-red-500 text-white hover:bg-red-400",
    blue: "bg-blue-500 text-white hover:bg-blue-400",
    orange: "bg-orange-500 text-slate-950 hover:bg-orange-400",
    amber: "bg-amber-500 text-slate-950 hover:bg-amber-400",
    yellow: "bg-yellow-400 text-slate-950 hover:bg-yellow-300",
    rose: "bg-rose-500 text-white hover:bg-rose-400",
    cyan: "bg-cyan-500 text-slate-950 hover:bg-cyan-400",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "rounded-xl px-3 py-2 text-sm font-bold transition",
        disabled ? "cursor-not-allowed opacity-45" : "",
        variants[variant] || variants.dark,
        className,
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function ChronoDisplay({ chrono, chronoClassName = "" }) {
  const minutes = String(Math.floor(chrono / 60)).padStart(2, "0");
  const seconds = String(chrono % 60).padStart(2, "0");

  return (
    <div
      className={`font-mono font-black tabular-nums text-[clamp(3rem,8vw,6rem)] leading-none ${chronoClassName}`}
    >
      {minutes}:{seconds}
    </div>
  );
}

function CounterPad({
  label,
  value,
  onIncrement,
  onDecrement,
  accent = "slate",
  widthClass = "w-[82px]",
}) {
  const accentMap = {
    slate: "bg-white/10 border-white/10",
    red: "bg-red-500/15 border-red-300/20",
    blue: "bg-blue-500/15 border-blue-300/20",
    emerald: "bg-emerald-500/15 border-emerald-300/20",
    amber: "bg-amber-500/15 border-amber-300/20",
    yellow: "bg-yellow-500/15 border-yellow-300/20",
  };

  return (
    <div
      className={`rounded-2xl border p-2 ${widthClass} ${accentMap[accent] || accentMap.slate}`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/65 text-center font-bold">
        {label}
      </div>

      <div className="mt-2 flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={onIncrement}
          className="w-full rounded-lg bg-white text-black font-black text-lg leading-none py-1 hover:bg-white/90"
        >
          +
        </button>

        <div className="w-full rounded-lg bg-black/35 py-2 text-center text-2xl font-black leading-none text-white">
          {value}
        </div>

        <button
          type="button"
          onClick={onDecrement}
          className="w-full rounded-lg bg-white/14 text-white font-black text-lg leading-none py-1 hover:bg-white/20"
        >
          −
        </button>
      </div>
    </div>
  );
}

function StateToggle({ label, active, onClick, tone = "dark" }) {
  const tones = {
    dark: active
      ? "bg-white text-black"
      : "bg-white/10 text-white border border-white/10 hover:bg-white/15",
    green: active
      ? "bg-emerald-400 text-slate-950"
      : "bg-emerald-500/15 text-emerald-200 border border-emerald-400/20 hover:bg-emerald-500/25",
    rose: active
      ? "bg-rose-500 text-white"
      : "bg-rose-500/15 text-rose-200 border border-rose-400/20 hover:bg-rose-500/25",
    orange: active
      ? "bg-orange-400 text-slate-950"
      : "bg-orange-500/15 text-orange-200 border border-orange-400/20 hover:bg-orange-500/25",
    amber: active
      ? "bg-amber-400 text-slate-950"
      : "bg-amber-500/15 text-amber-200 border border-amber-400/20 hover:bg-amber-500/25",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl px-3 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${tones[tone] || tones.dark}`}
    >
      {label}
    </button>
  );
}

function SimpleFighterRow({
  label,
  competiteur,
  score,
  color = "red",
  onAddAdvantage,
  onRemoveAdvantage,
  onAddPoints2,
  onRemovePoints2,
  onAddPoints3,
  onRemovePoints3,
  onAddPoints4,
  onRemovePoints4,
  onAddPenalty,
  onRemovePenalty,
  onToggleIppon,
  onToggleDisqualification,
  onToggleMedicalStop,
  onToggleForfait,
}) {
  const pts = getNeWazaPoints(score || {});
  const club = getCompetiteurClub(competiteur);
  const isRed = color === "red";

  const count2 =
    (score?.takedown2 || 0) +
    (score?.kneeOnBelly2 || 0) +
    (score?.sweep2 || 0) +
    (score?.other2 || 0);

  const count3 = (score?.guardPass3 || 0) + (score?.other3 || 0);
  const count4 =
    (score?.mount4 || 0) + (score?.backControl4 || 0) + (score?.other4 || 0);

  const theme = isRed
    ? {
        wrapper:
          "border-red-400/30 bg-[linear-gradient(90deg,rgba(127,29,29,.96)_0%,rgba(220,38,38,.80)_62%,rgba(239,68,68,.70)_100%)]",
        label: "text-red-100/80",
        totalBg: "bg-black/22",
        counterAccent: "red",
      }
    : {
        wrapper:
          "border-blue-400/30 bg-[linear-gradient(90deg,rgba(30,58,138,.96)_0%,rgba(37,99,235,.80)_62%,rgba(59,130,246,.70)_100%)]",
        label: "text-blue-100/80",
        totalBg: "bg-black/22",
        counterAccent: "blue",
      };

  return (
    <div className={`rounded-[28px] border ${theme.wrapper} p-4 md:p-5 text-white shadow-2xl`}>
      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_auto_auto_auto_auto_auto_auto_180px] gap-3 items-center">
        <div className="min-w-0">
          <div className={`text-[11px] uppercase tracking-[0.25em] font-bold ${theme.label}`}>
            {label}
          </div>
          <div className="mt-2 text-[clamp(1.2rem,2vw,2rem)] font-black leading-tight break-words">
            {getCompetiteurDisplayName(competiteur, label)}
          </div>
          {!!club && (
            <div className="mt-1 text-sm md:text-base text-white/80 font-semibold truncate">
              {club}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <StateToggle
              label="Ippon"
              active={Boolean(score?.submission)}
              onClick={onToggleIppon}
              tone="green"
            />
            <StateToggle
              label="Médical"
              active={Boolean(score?.medicalStop)}
              onClick={onToggleMedicalStop}
              tone="orange"
            />
            <StateToggle
              label="Forfait"
              active={Boolean(score?.forfait)}
              onClick={onToggleForfait}
              tone="amber"
            />
            <StateToggle
              label="Disq."
              active={Boolean(isDisqualified(score))}
              onClick={onToggleDisqualification}
              tone="rose"
            />
          </div>
        </div>

        <CounterPad
          label="Avantage"
          value={score?.advantages || 0}
          onIncrement={onAddAdvantage}
          onDecrement={onRemoveAdvantage}
          accent="emerald"
        />

        <CounterPad
          label="+2"
          value={count2}
          onIncrement={onAddPoints2}
          onDecrement={onRemovePoints2}
          accent={theme.counterAccent}
        />

        <CounterPad
          label="+3"
          value={count3}
          onIncrement={onAddPoints3}
          onDecrement={onRemovePoints3}
          accent={theme.counterAccent}
        />

        <CounterPad
          label="+4"
          value={count4}
          onIncrement={onAddPoints4}
          onDecrement={onRemovePoints4}
          accent={theme.counterAccent}
        />

        <CounterPad
          label="Pénalité"
          value={score?.penalties || 0}
          onIncrement={onAddPenalty}
          onDecrement={onRemovePenalty}
          accent="amber"
        />

        <div className="hidden xl:flex flex-col gap-2 justify-center">
          <Badge tone="slate">
            2 pts bonus pén.
            {" "}
            {score?.penaltyBonus2 || 0}
          </Badge>
        </div>

        <div
          className={`rounded-[24px] ${theme.totalBg} px-4 py-4 md:px-5 md:py-5 text-center min-w-[160px]`}
        >
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-bold">
            Total
          </div>
          <div className="mt-2 text-[clamp(3rem,6vw,5rem)] font-black leading-none tabular-nums">
            {pts.total}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main                                                                       */
/* -------------------------------------------------------------------------- */

export default function ScoreboardAdminNeWaza({
  tapisId,
  competitionId,
  competiteur1,
  competiteur2,
  categorieAge,
  categoriePoids = "",
  combatKey,
  discipline,
  onValidate,
  dureeCombat,
}) {
  const defaultDuration = useMemo(() => {
    if (Number.isFinite(Number(dureeCombat)) && Number(dureeCombat) > 0) {
      return Number(dureeCombat);
    }
    return getDefaultDurationFromCategory(categorieAge);
  }, [dureeCombat, categorieAge]);

  const [redScore, setRedScore] = useState(() => createEmptyNeWazaScore());
  const [blueScore, setBlueScore] = useState(() => createEmptyNeWazaScore());

  const [chrono, setChrono] = useState(defaultDuration);
  const [chronoMax, setChronoMax] = useState(defaultDuration);
  const [isChronoRunning, setIsChronoRunning] = useState(false);
  const [isGoldenScore, setIsGoldenScore] = useState(false);

  const [medicalCallActive, setMedicalCallActive] = useState(false);
  const [videoReviewActive, setVideoReviewActive] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);

  const titreDiscipline = discipline || "Ne-Waza";
  const publicCombatLabel = useMemo(
    () => sanitizeCombatLabel(combatKey),
    [combatKey],
  );

  const liveResult = useMemo(() => {
    try {
      return calculateNeWazaResult(redScore, blueScore, {
        isGoldenScore,
      });
    } catch (error) {
      console.error("Erreur calcul live Ne-Waza :", error);
      return null;
    }
  }, [redScore, blueScore, isGoldenScore]);

  const winnerColor = liveResult?.winnerColor || null;

  const chronoPercent = useMemo(() => {
    if (isGoldenScore) return 100;
    if (!chronoMax) return 0;
    return Math.max(0, Math.min(100, (chrono / chronoMax) * 100));
  }, [chrono, chronoMax, isGoldenScore]);

  const showNeedGoldenBanner = Boolean(
    !isGoldenScore && liveResult?.needGoldenScore && chrono <= 0,
  );

  const chronoColorClass = useMemo(() => {
    if (isGoldenScore) return "text-yellow-300";
    if (showNeedGoldenBanner) return "text-yellow-300";
    if (chrono <= 10) return "text-red-400";
    if (chrono <= 30) return "text-amber-300";
    return "text-white";
  }, [chrono, isGoldenScore, showNeedGoldenBanner]);

  const statusReason = useMemo(() => {
    return getSimpleReasonLabel(liveResult, redScore, blueScore, isGoldenScore);
  }, [liveResult, redScore, blueScore, isGoldenScore]);

  useEffect(() => {
    if (!isGoldenScore) {
      setChrono(defaultDuration);
      setChronoMax(defaultDuration);
    }
  }, [defaultDuration, isGoldenScore]);

  useEffect(() => {
    if (!competitionId || !tapisId) return;
    const roomKey = `${competitionId}_${tapisId}`;
    socket.emit("join_combat", roomKey);
  }, [competitionId, tapisId]);

  useEffect(() => {
    if (!isChronoRunning) return;

    const id = setInterval(() => {
      setChrono((prev) => {
        if (isGoldenScore) {
          return prev + 1;
        }

        if (prev <= 1) {
          clearInterval(id);
          setIsChronoRunning(false);
          toast.info("⏰ Fin du temps réglementaire");
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [isChronoRunning, isGoldenScore]);

  useEffect(() => {
    if (!competitionId || !tapisId) return;

    const roomKey = `${competitionId}_${tapisId}`;

    socket.emit("score_update", {
      combatId: roomKey,
      data: {
        discipline: discipline || "Ne-Waza",
        competiteur1,
        competiteur2,
        redScore,
        blueScore,
        chrono,
        chronoMax,
        chronoMode: isGoldenScore ? "countup" : "countdown",
        isGoldenScore,
        categorieAge: categorieAge || "",
        categoriePoids: categoriePoids || "",
        combatLabel: publicCombatLabel,
        winnerColor,
        needGoldenScore: !isGoldenScore && !!liveResult?.needGoldenScore,
        medicalCallActive,
        videoReviewActive,
        message: isGoldenScore ? "Golden score" : "",
      },
    });
  }, [
    competitionId,
    tapisId,
    discipline,
    competiteur1,
    competiteur2,
    redScore,
    blueScore,
    chrono,
    chronoMax,
    isGoldenScore,
    categorieAge,
    categoriePoids,
    publicCombatLabel,
    winnerColor,
    liveResult,
    medicalCallActive,
    videoReviewActive,
  ]);

  const handleStartPause = () => {
    if (!isGoldenScore && chrono <= 0) {
      toast.info("Le chrono est à 0. Réinitialise-le ou lance le golden score.");
      return;
    }
    setIsChronoRunning((prev) => !prev);
  };

  const handleResetChrono = () => {
    setIsChronoRunning(false);
    setChrono(isGoldenScore ? 0 : chronoMax);
  };

  const resetAll = useCallback(() => {
    setIsChronoRunning(false);
    setIsGoldenScore(false);
    setMedicalCallActive(false);
    setVideoReviewActive(false);
    setChrono(defaultDuration);
    setChronoMax(defaultDuration);
    setRedScore(createEmptyNeWazaScore());
    setBlueScore(createEmptyNeWazaScore());
    setModalVisible(false);
    toast.success("Scoreboard Ne-Waza réinitialisé.");
  }, [defaultDuration]);

  const handleClearTerminalStates = useCallback(() => {
    setRedScore((prev) => clearTerminalStates(prev));
    setBlueScore((prev) => clearTerminalStates(prev));
    toast.info("États finaux effacés.");
  }, []);

  const setExclusiveTerminalState = useCallback(
    (side, type) => {
      let nextRed = clearTerminalStates(redScore);
      let nextBlue = clearTerminalStates(blueScore);

      if (side === "red") {
        if (type === "submission") nextRed = setSubmission(nextRed, true);
        if (type === "disqualification") nextRed = setDisqualification(nextRed, true);
        if (type === "medicalStop") nextRed = setMedicalStop(nextRed, true);
        if (type === "forfait") nextRed = setForfait(nextRed, true);
      } else {
        if (type === "submission") nextBlue = setSubmission(nextBlue, true);
        if (type === "disqualification") nextBlue = setDisqualification(nextBlue, true);
        if (type === "medicalStop") nextBlue = setMedicalStop(nextBlue, true);
        if (type === "forfait") nextBlue = setForfait(nextBlue, true);
      }

      setRedScore(nextRed);
      setBlueScore(nextBlue);
    },
    [redScore, blueScore],
  );

  const toggleTerminalState = (side, type) => {
    const score = side === "red" ? redScore : blueScore;

    const isActive =
      (type === "submission" && !!score?.submission) ||
      (type === "disqualification" && !!isDisqualified(score)) ||
      (type === "medicalStop" && !!score?.medicalStop) ||
      (type === "forfait" && !!score?.forfait);

    if (isActive) {
      if (side === "red") {
        setRedScore((prev) => clearOneSideTerminalStates(prev));
      } else {
        setBlueScore((prev) => clearOneSideTerminalStates(prev));
      }
      return;
    }

    setExclusiveTerminalState(side, type);
  };

  const handleAddTechnique = (who, kind) => {
    if (who === "red") {
      setRedScore((prev) => addTechnique(prev, { kind }));
    } else {
      setBlueScore((prev) => addTechnique(prev, { kind }));
    }
  };

  const handleRemoveTechnique = (who, kind) => {
    if (who === "red") {
      setRedScore((prev) => removeTechnique(prev, { kind }));
    } else {
      setBlueScore((prev) => removeTechnique(prev, { kind }));
    }
  };

  const handleAddAdvantage = (who) => {
    if (who === "red") {
      setRedScore((prev) => addAdvantage(prev));
    } else {
      setBlueScore((prev) => addAdvantage(prev));
    }
  };

  const handleRemoveAdvantage = (who) => {
    if (who === "red") {
      setRedScore((prev) => removeAdvantage(prev));
    } else {
      setBlueScore((prev) => removeAdvantage(prev));
    }
  };

  const handleAddPenalty = (penalized) => {
    if (penalized === "red") {
      const { penalized: nextRed, opponent: nextBlue } = addPenalty(
        redScore,
        blueScore,
      );
      setRedScore(nextRed);
      setBlueScore(nextBlue);
    } else {
      const { penalized: nextBlue, opponent: nextRed } = addPenalty(
        blueScore,
        redScore,
      );
      setBlueScore(nextBlue);
      setRedScore(nextRed);
    }
  };

  const handleRemovePenalty = (penalized) => {
    if (penalized === "red") {
      const { penalized: nextRed, opponent: nextBlue } = removePenalty(
        redScore,
        blueScore,
      );
      setRedScore(nextRed);
      setBlueScore(nextBlue);
    } else {
      const { penalized: nextBlue, opponent: nextRed } = removePenalty(
        blueScore,
        redScore,
      );
      setBlueScore(nextBlue);
      setRedScore(nextRed);
    }
  };

  const handleRequestGoldenScore = () => {
    setIsChronoRunning(false);
    setIsGoldenScore(true);
    setChronoMax(0);
    setChrono(0);
    setModalVisible(false);
    toast.info("Golden score lancé.");
  };

  const handleValidate = () => {
    const result = liveResult || calculateNeWazaResult(redScore, blueScore, {
      isGoldenScore,
    });

    if (result?.needGoldenScore && !isGoldenScore) {
      toast.info("Égalité parfaite : golden score nécessaire.");
      return;
    }

    if (result?.needRefereeDecision) {
      toast.error("Cas exceptionnel : validation automatique impossible.");
      return;
    }

    const payload = {
      result1: result.result1,
      result2: result.result2,
      points1: result.points1,
      points2: result.points2,
      advantages1: result.advantages1,
      advantages2: result.advantages2,
      penalties1: result.penalties1,
      penalties2: result.penalties2,

      submission1: redScore.submission || false,
      submission2: blueScore.submission || false,

      disqualification1: isDisqualified(redScore),
      disqualification2: isDisqualified(blueScore),

      forfait1: redScore.forfait || false,
      forfait2: blueScore.forfait || false,

      medical1: redScore.medicalStop || false,
      medical2: blueScore.medicalStop || false,

      golden_score: isGoldenScore,

      details_combat: buildDetailsCombatNeWaza({
        redScore,
        blueScore,
        result,
        competiteur1,
        competiteur2,
        chrono,
        chronoMax,
        isGoldenScore,
        medicalCallActive,
        videoReviewActive,
      }),
    };

    if (typeof onValidate === "function") {
      onValidate(payload);
      toast.success("Résultat Ne-Waza validé.");
    } else {
      console.log("[Ne-Waza] Résultat calculé :", payload);
      toast.success("Résultat Ne-Waza calculé (voir console).");
    }
  };

  return (
    <div className="h-full min-h-0 bg-black text-white overflow-hidden">
      <ToastContainer position="top-center" />

      <div className="h-full min-h-0 overflow-y-auto px-3 md:px-4 py-3 pb-28">
        <div className="mx-auto w-full max-w-[1800px] space-y-4">
          {/* Header */}
          <div className="rounded-[24px] border border-white/10 bg-slate-950 px-4 md:px-5 py-4">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">
                  Tableau de score
                </div>
                <div className="mt-2 text-[clamp(1.2rem,2.2vw,2rem)] font-black leading-tight">
                  {titreDiscipline}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge tone="emerald">Tapis {tapisId}</Badge>
                  {!!categorieAge && <Badge tone="yellow">Cat. {categorieAge}</Badge>}
                  {!!categoriePoids && <Badge tone="slate">{categoriePoids}</Badge>}
                  {isGoldenScore && <Badge tone="yellow">Golden score</Badge>}
                  {winnerColor === "red" && <Badge tone="red">Avantage Rouge</Badge>}
                  {winnerColor === "blue" && <Badge tone="blue">Avantage Bleu</Badge>}
                  {!winnerColor && <Badge tone="slate">{statusReason}</Badge>}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                <ActionButton variant="dark" onClick={handleClearTerminalStates}>
                  Effacer états
                </ActionButton>
                <ActionButton variant="dark" onClick={resetAll}>
                  Réinitialiser
                </ActionButton>
                <ActionButton
                  variant="green"
                  onClick={() => setModalVisible(true)}
                >
                  Valider le combat
                </ActionButton>
              </div>
            </div>
          </div>

          {/* Central controls */}
          <div className="rounded-[28px] border border-white/10 bg-slate-950 p-4 md:p-5">
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-5 items-center">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                  Chronomètre principal
                </div>

                <div className="mt-3">
                  <ChronoDisplay
                    chrono={chrono}
                    chronoClassName={chronoColorClass}
                  />
                </div>

                <div className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/50">
                  {isGoldenScore
                    ? "Temps écoulé en golden score"
                    : `Temps réglementaire : ${formatSeconds(chronoMax)}`}
                </div>

                <div className="mt-4 h-4 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className={`h-full transition-all duration-300 ${
                      isGoldenScore ? "bg-yellow-400" : "bg-emerald-400"
                    }`}
                    style={{ width: `${chronoPercent}%` }}
                  />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <ActionButton variant="green" onClick={handleStartPause}>
                    {isChronoRunning ? "Pause" : "Start"}
                  </ActionButton>
                  <ActionButton variant="dark" onClick={handleResetChrono}>
                    Reset chrono
                  </ActionButton>
                  <ActionButton variant="yellow" onClick={handleRequestGoldenScore}>
                    Golden score
                  </ActionButton>
                  <ActionButton
                    variant={medicalCallActive ? "orange" : "dark"}
                    onClick={() => setMedicalCallActive((prev) => !prev)}
                  >
                    Appel médical
                  </ActionButton>
                  <ActionButton
                    variant={videoReviewActive ? "cyan" : "dark"}
                    onClick={() => setVideoReviewActive((prev) => !prev)}
                  >
                    Challenge vidéo
                  </ActionButton>
                </div>
              </div>

              <div className="flex flex-col items-start xl:items-end gap-2">
                {!!publicCombatLabel && <Badge tone="slate">{publicCombatLabel}</Badge>}
                {medicalCallActive && <Badge tone="orange">Médecin appelé</Badge>}
                {videoReviewActive && <Badge tone="cyan">Vidéo en cours</Badge>}
                {showNeedGoldenBanner && (
                  <Badge tone="yellow">Égalité parfaite — lancer golden score</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Red row */}
          <SimpleFighterRow
            label="Rouge"
            competiteur={competiteur1}
            score={redScore}
            color="red"
            onAddAdvantage={() => handleAddAdvantage("red")}
            onRemoveAdvantage={() => handleRemoveAdvantage("red")}
            onAddPoints2={() => handleAddTechnique("red", "other2")}
            onRemovePoints2={() => handleRemoveTechnique("red", "other2")}
            onAddPoints3={() => handleAddTechnique("red", "other3")}
            onRemovePoints3={() => handleRemoveTechnique("red", "other3")}
            onAddPoints4={() => handleAddTechnique("red", "other4")}
            onRemovePoints4={() => handleRemoveTechnique("red", "other4")}
            onAddPenalty={() => handleAddPenalty("red")}
            onRemovePenalty={() => handleRemovePenalty("red")}
            onToggleIppon={() => toggleTerminalState("red", "submission")}
            onToggleDisqualification={() =>
              toggleTerminalState("red", "disqualification")
            }
            onToggleMedicalStop={() => toggleTerminalState("red", "medicalStop")}
            onToggleForfait={() => toggleTerminalState("red", "forfait")}
          />

          {/* Blue row */}
          <SimpleFighterRow
            label="Bleu"
            competiteur={competiteur2}
            score={blueScore}
            color="blue"
            onAddAdvantage={() => handleAddAdvantage("blue")}
            onRemoveAdvantage={() => handleRemoveAdvantage("blue")}
            onAddPoints2={() => handleAddTechnique("blue", "other2")}
            onRemovePoints2={() => handleRemoveTechnique("blue", "other2")}
            onAddPoints3={() => handleAddTechnique("blue", "other3")}
            onRemovePoints3={() => handleRemoveTechnique("blue", "other3")}
            onAddPoints4={() => handleAddTechnique("blue", "other4")}
            onRemovePoints4={() => handleRemoveTechnique("blue", "other4")}
            onAddPenalty={() => handleAddPenalty("blue")}
            onRemovePenalty={() => handleRemovePenalty("blue")}
            onToggleIppon={() => toggleTerminalState("blue", "submission")}
            onToggleDisqualification={() =>
              toggleTerminalState("blue", "disqualification")
            }
            onToggleMedicalStop={() => toggleTerminalState("blue", "medicalStop")}
            onToggleForfait={() => toggleTerminalState("blue", "forfait")}
          />
        </div>
      </div>

      <DetermineWinnerModalNeWaza
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        onValidate={handleValidate}
        onRequestGoldenScore={handleRequestGoldenScore}
        competiteur1={competiteur1}
        competiteur2={competiteur2}
        redScore={redScore}
        blueScore={blueScore}
        autoResult={liveResult}
        chrono={chrono}
      />
    </div>
  );
}